import { apiClient } from '@/api/axios';
import { endpoints } from '@/api/endpoints';
import { featureFlagsMockData } from '@/data/featureFlagsMockData';
import { featureFlagCache } from '@/feature-flags/FeatureCache';
import {
  evaluateFeatureFlag,
  type FeatureEvaluationResult,
  type FeatureFlag,
} from '@/feature-flags/FeatureEvaluator';
import type {
  FeatureEvaluationContext,
  FeatureFlagEnvironment,
} from '@/feature-flags/FeatureStrategies';

export interface FeatureFlagQueryParams {
  search?: string;
  status?: 'all' | 'enabled' | 'disabled' | 'locked';
  productId?: string;
  environment?: FeatureFlagEnvironment | 'all';
  type?: string;
  owner?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'key' | 'rolloutPercentage' | 'lastModified';
  sortDirection?: 'asc' | 'desc';
}

export interface FeatureFlagListResult {
  items: FeatureFlag[];
  total: number;
  page: number;
  pageSize: number;
}

type Subscriber = (flags: FeatureFlag[]) => void;

const defaultDelayMs = 120;

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), defaultDelayMs);
  });
}

function normalize(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function sortFlags(
  flags: FeatureFlag[],
  sortBy: FeatureFlagQueryParams['sortBy'],
  sortDirection: FeatureFlagQueryParams['sortDirection'],
): FeatureFlag[] {
  const by = sortBy ?? 'lastModified';
  const direction = sortDirection ?? 'desc';

  const sorted = [...flags].sort((left, right) => {
    if (by === 'key') {
      return left.key.localeCompare(right.key);
    }

    if (by === 'rolloutPercentage') {
      return left.rolloutPercentage - right.rolloutPercentage;
    }

    return (
      new Date(left.lastModified).getTime() -
      new Date(right.lastModified).getTime()
    );
  });

  return direction === 'asc' ? sorted : sorted.reverse();
}

function filterFlags(
  flags: FeatureFlag[],
  params: FeatureFlagQueryParams,
): FeatureFlag[] {
  const search = normalize(params.search);
  const owner = normalize(params.owner);

  return flags.filter((flag) => {
    if (params.status && params.status !== 'all') {
      const activeStatus = flag.status === 'enabled' ? 'enabled' : 'disabled';

      if (params.status === 'locked' && flag.status !== 'locked') {
        return false;
      }

      if (params.status !== 'locked' && activeStatus !== params.status) {
        return false;
      }
    }

    if (params.productId && params.productId !== 'all') {
      if ((flag.productId ?? 'global') !== params.productId) {
        return false;
      }
    }

    if (params.environment && params.environment !== 'all') {
      if (flag.environment !== params.environment) {
        return false;
      }
    }

    if (params.type && params.type !== 'all') {
      if (flag.strategy !== params.type) {
        return false;
      }
    }

    if (owner && !normalize(flag.owner).includes(owner)) {
      return false;
    }

    if (!search) {
      return true;
    }

    const text = [
      flag.key,
      flag.name,
      flag.description,
      flag.owner,
      ...(flag.tags ?? []),
    ]
      .join(' ')
      .toLowerCase();

    return text.includes(search);
  });
}

function seedFlags(): FeatureFlag[] {
  const cached = featureFlagCache.read();

  if (cached.length) {
    return cached;
  }

  featureFlagCache.write(featureFlagsMockData);
  return featureFlagsMockData;
}

class FeatureFlagService {
  private flags: FeatureFlag[] = seedFlags();

  private readonly subscribers = new Set<Subscriber>();

  async query(
    params: FeatureFlagQueryParams = {},
  ): Promise<FeatureFlagListResult> {
    try {
      const response = await apiClient.get<FeatureFlagListResult>(
        endpoints.featureFlags,
        {
          params,
        },
      );

      this.flags = response.data.items;
      featureFlagCache.write(this.flags);
      this.notify();

      return response.data;
    } catch {
      const filtered = filterFlags(
        sortFlags(this.flags, params.sortBy, params.sortDirection),
        params,
      );
      const page = params.page ?? 1;
      const pageSize = params.pageSize ?? 8;
      const start = (page - 1) * pageSize;

      return delay({
        items: filtered.slice(start, start + pageSize),
        total: filtered.length,
        page,
        pageSize,
      });
    }
  }

  async create(
    payload: Omit<FeatureFlag, 'id' | 'createdDate' | 'lastModified'>,
  ): Promise<FeatureFlag> {
    const now = new Date().toISOString();

    const created: FeatureFlag = {
      ...payload,
      id: crypto.randomUUID(),
      createdDate: now,
      lastModified: now,
    };

    try {
      await apiClient.post(endpoints.featureFlags, created);
    } catch {
      // local fallback mode
    }

    this.flags = [created, ...this.flags];
    featureFlagCache.write(this.flags);
    this.notify();

    return created;
  }

  async update(
    id: string,
    payload: Partial<FeatureFlag>,
  ): Promise<FeatureFlag | null> {
    const current = this.flags.find((flag) => flag.id === id);

    if (!current) {
      return null;
    }

    const updated: FeatureFlag = {
      ...current,
      ...payload,
      lastModified: new Date().toISOString(),
    };

    try {
      await apiClient.put(endpoints.featureFlagById(id), updated);
    } catch {
      // local fallback mode
    }

    this.flags = this.flags.map((flag) => (flag.id === id ? updated : flag));
    featureFlagCache.write(this.flags);
    this.notify();

    return updated;
  }

  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(endpoints.featureFlagById(id));
    } catch {
      // local fallback mode
    }

    this.flags = this.flags.filter((flag) => flag.id !== id);
    featureFlagCache.write(this.flags);
    this.notify();
  }

  async updateRollout(id: string, rolloutPercentage: number): Promise<void> {
    const current = this.flags.find((flag) => flag.id === id);

    if (!current) {
      return;
    }

    const next = {
      ...current,
      rolloutPercentage,
      lastModified: new Date().toISOString(),
    };

    try {
      await apiClient.put(endpoints.featureFlagRollout(id), {
        rolloutPercentage,
      });
    } catch {
      // local fallback mode
    }

    this.flags = this.flags.map((flag) => (flag.id === id ? next : flag));
    featureFlagCache.write(this.flags);
    this.notify();
  }

  async getEnvironments(): Promise<FeatureFlagEnvironment[]> {
    try {
      const response = await apiClient.get<FeatureFlagEnvironment[]>(
        endpoints.featureFlagsEnvironments,
      );
      return response.data;
    } catch {
      return delay(['development', 'sandbox', 'stage', 'production']);
    }
  }

  async getProducts(): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await apiClient.get<Array<{ id: string; name: string }>>(
        endpoints.featureFlagsProducts,
      );
      return response.data;
    } catch {
      return delay([
        { id: 'global', name: 'Global' },
        { id: 'admin-portal', name: 'Admin Portal' },
        { id: 'credit-reports', name: 'Credit Reports' },
        { id: 'embedded-framework', name: 'Embedded Framework' },
        { id: 'notification-center', name: 'Notification Center' },
      ]);
    }
  }

  isEnabled(key: string, context: FeatureEvaluationContext = {}): boolean {
    return this.evaluate(key, context).enabled;
  }

  evaluate(
    key: string,
    context: FeatureEvaluationContext = {},
  ): FeatureEvaluationResult {
    const flag = this.flags.find((item) => item.key === key) ?? null;

    return evaluateFeatureFlag(flag, context);
  }

  async refresh(): Promise<void> {
    const result = await this.query({ page: 1, pageSize: 250 });
    this.flags = result.items;
    featureFlagCache.write(this.flags);
    this.notify();
  }

  subscribe(subscriber: Subscriber): () => void {
    this.subscribers.add(subscriber);

    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  getSnapshot(): FeatureFlag[] {
    return [...this.flags];
  }

  private notify(): void {
    const snapshot = this.getSnapshot();
    this.subscribers.forEach((subscriber) => subscriber(snapshot));
  }
}

export const featureFlagService = new FeatureFlagService();
